import React, { useMemo, useRef, useState, useEffect, useLayoutEffect } from 'react';
import { Platform, Pressable, ScrollView, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { Paprika } from '../../../../styles/theme';
import { MEALS, DAY_SHORT_KEYS, dayShortKey, dayFullKey, slotKey, rowOf, dayOf } from '../constants';
import { useTranslation } from 'react-i18next';
import { SlotCard } from './SlotCard';
import { SlotExtraCard } from './SlotExtraCard';
import { EmptySlot } from './EmptySlot';
import { EntryCard } from './EntryCard';
import { SlotPreviewCard } from './SlotPreviewCard';
import { SlotSideOffer } from './SlotSideOffer';
import { SlotMiniCell } from './SlotMiniCell';
import { COMPACT_ROW_HEIGHT, openRowHeight, type FocusRow } from './rowFocus';
import { animateRowSwap, prefersReducedMotion } from './rowAnimation';
import { canAddDish } from '../slotMeals';
import type { SideOption } from '../useSideSuggestions';
import { isEntryMeal, isSideableEntryMeal } from '../../../../redux/mealEntry';
import { proposalsOf, slotHasAcceptedMeal } from '../../proposalState';
import type { PlannedMeal } from '../../../../redux/mealPlanSlice';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import { todayIso, addDays, parseIsoDate } from '../../../../utils/localDate';

interface Anchor {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Props {
  weekStart: string;
  slotsMap: Map<number, PlannedMeal>;
  /** Every meal of a slot — extra dishes render as compact rows under the card. */
  slotMeals?: Map<number, PlannedMeal[]>;
  recipeIndex: Record<string, RecipeIndexEntry>;
  sel: number | null;
  moving: boolean;
  flash: number | null;
  menuFor?: number | null;
  /**
   * Slot holding the next dish to cook (#579). Its card gets a terracotta ring
   * and its column an "Aujourd'hui" pill, so the bar above the grid tells you
   * *what* and the grid tells you *where it lives*. Null when there is none.
   */
  nextSk?: number | null;
  onSlotPress: (sk: number) => void;
  onPhotoPress: (sk: number) => void;
  onEmptyPress?: (sk: number, anchor: Anchor) => void;
  onEntryPress?: (sk: number, anchor: Anchor) => void;
  onRemoveSlot?: (sk: number) => void;
  onRemoveExtra?: (meal: PlannedMeal) => void;
  onCardDrop?: (srcSk: number, targetSk: number) => void;
  /** Opens the detail drawer for a non-primary dish of a slot. */
  onExtraPress?: (meal: PlannedMeal) => void;
  /**
   * The slot the Library drawer is filling. Its column header turns
   * terracotta, an empty slot glows, and `preview` — the recipe hovered in
   * the drawer — is drawn in it faded, over whatever it holds now.
   */
  placeTarget?: { sk: number; preview: RecipeIndexEntry | null } | null;
  /**
   * The accompaniment a cell offers under its dish (#746). Asked for the
   * hovered cell only — ranking the library for 14 cells on every render would
   * be paid whether or not anyone looked.
   */
  sideFor?: (sk: number) => SideOption | null;
  onAddSide?: (sk: number, side: SideOption) => void;
  /** Opens the accompaniment list for a cell. */
  onMoreSides?: (sk: number) => void;
  /**
   * The open row (rowFocus.ts). The other two are drawn as 50px strips, and
   * anything clicked in a strip opens its row through `onFocusRow`.
   */
  focusRow: FocusRow;
  onFocusRow: (row: FocusRow, via: 'card' | 'label' | 'add' | 'drag') => void;
  /**
   * « + Ajouter » under a day's header, for a day with a meal still free.
   * It opens the drawer on that day's likeliest meal (dayAddTarget.ts).
   */
  canAddToDay?: (di: number) => boolean;
  onAddToDay?: (di: number) => void;
}

// How long a drag must hover a closed row before it opens, so a card dragged
// across the grid to dinner does not open every row it passes over.
const DRAG_OPEN_DELAY_MS = 450;

const isWeb = Platform.OS === 'web';
// The offer is revealed by hover. A touch-only device (an iPad in landscape
// gets this grid) has none, so it draws the plain row in the cell instead.
const canHover =
  isWeb && typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover)').matches;

// Breakfast is only valid for drop on weekends (dayOf >= 5); weekday breakfast cells (dayOf < 5) reject drops
function isExcludedFromDrop(sk: number): boolean {
  return rowOf(sk) === 0 && dayOf(sk) < 5;
}

export function SlotGrid({ weekStart, slotsMap, slotMeals, recipeIndex, sel, moving, flash, menuFor, nextSk, onSlotPress, onPhotoPress, onEmptyPress, onEntryPress, onRemoveSlot, onRemoveExtra, onCardDrop, onExtraPress, placeTarget, sideFor, onAddSide, onMoreSides, focusRow, onFocusRow, canAddToDay, onAddToDay }: Props) {
  const { t } = useTranslation();
  const cellRefs = useRef<Record<number, View | null>>({});
  const [dragOverSk, setDragOverSk] = useState<number | null>(null);
  const [hoverSk, setHoverSk] = useState<number | null>(null);
  const [viewportH, setViewportH] = useState(0);
  const [headerH, setHeaderH] = useState(0);
  // Everything in the scroll area that is not a row: its padding (2 × 20), the
  // day header and its margin, the content gap after the header and between
  // rows (3 × 8), and each row's bottom margin (3 × 8).
  const openH = openRowHeight(viewportH, 40 + headerH + 4 + 24 + 24);

  // The drop listeners are attached once; they read the open row through refs.
  const focusRef = useRef(focusRow);
  focusRef.current = focusRow;
  const onFocusRowRef = useRef(onFocusRow);
  onFocusRowRef.current = onFocusRow;
  const dragOpen = useRef<{ row: number; timer: ReturnType<typeof setTimeout> } | null>(null);

  // A change of open row is animated from where the rows were to where they
  // are (rowAnimation.ts). "Where they were" is read during the render that
  // changes it — the DOM is still the old one then — so the grid pays for no
  // layout read on the renders a hover causes.
  const rowRefs = useRef<Array<any>>([]);
  const committedFocus = useRef(focusRow);
  const heightsBefore = useRef<number[] | null>(null);
  const measureRows = () => [0, 1, 2].map((i) => rowRefs.current[i]?.getBoundingClientRect?.().height ?? 0);
  if (isWeb && focusRow !== committedFocus.current && heightsBefore.current === null) {
    heightsBefore.current = measureRows();
  }
  useLayoutEffect(() => {
    if (committedFocus.current === focusRow) return;
    committedFocus.current = focusRow;
    const before = heightsBefore.current;
    heightsBefore.current = null;
    if (!before || prefersReducedMotion()) return;
    animateRowSwap(rowRefs.current, before, measureRows(), focusRow);
  }, [focusRow]);
  const cancelDragOpen = () => {
    if (dragOpen.current) clearTimeout(dragOpen.current.timer);
    dragOpen.current = null;
  };
  const hoverSide = useMemo(
    () => (hoverSk !== null && sideFor ? sideFor(hoverSk) : null),
    [hoverSk, sideFor],
  );

  // Native enter/leave on the cell, not Pressable hover: the cell's children
  // are Pressables, and RNW un-hovers a parent the moment the pointer reaches
  // one (see SlotCard). The floating offer is a child of the cell, so the
  // pointer stays "inside" on its way down to it.
  useEffect(() => {
    if (!canHover) return;
    const cleanups: Array<() => void> = [];
    Object.entries(cellRefs.current).forEach(([skStr, ref]) => {
      const el = ref as any;
      if (!el?.addEventListener) return;
      const sk = parseInt(skStr);
      const enter = () => setHoverSk(sk);
      const leave = () => setHoverSk((current) => (current === sk ? null : current));
      el.addEventListener('pointerenter', enter);
      el.addEventListener('pointerleave', leave);
      cleanups.push(() => {
        el.removeEventListener('pointerenter', enter);
        el.removeEventListener('pointerleave', leave);
      });
    });
    return () => cleanups.forEach((fn) => fn());
  }, []);

  // Attach drop zone listeners to cell DOM nodes (skip excluded weekday breakfast cells)
  useEffect(() => {
    if (!isWeb) return;

    const cells = cellRefs.current;
    const handlers: Record<number, { dragover: (e: Event) => void; dragleave: (e: Event) => void; drop: (e: Event) => void }> = {};

    Object.entries(cells).forEach(([skStr, ref]) => {
      const sk = parseInt(skStr);

      // Skip attaching listeners to excluded cells (weekday breakfast)
      if (isExcludedFromDrop(sk)) return;

      const el = ref as any;
      if (!el) return;

      const handleDragOver = (evt: Event) => {
        const e = evt as DragEvent;
        e.preventDefault();
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'move';
        }
        setDragOverSk(sk);
        // Hovering a closed row with a dish opens it after a beat, so the dish
        // can be dropped onto a full-size card rather than a strip.
        const row = rowOf(sk);
        if (row === focusRef.current) {
          cancelDragOpen();
        } else if (dragOpen.current?.row !== row) {
          cancelDragOpen();
          dragOpen.current = {
            row,
            timer: setTimeout(() => {
              dragOpen.current = null;
              onFocusRowRef.current(row as FocusRow, 'drag');
            }, DRAG_OPEN_DELAY_MS),
          };
        }
      };

      const handleDragLeave = (evt: Event) => {
        const e = evt as DragEvent;
        const relatedTarget = e.relatedTarget as HTMLElement | null;
        if (relatedTarget && el.contains(relatedTarget)) return;
        setDragOverSk(null);
      };

      const handleDrop = (evt: Event) => {
        const e = evt as DragEvent;
        e.preventDefault();
        setDragOverSk(null);
        cancelDragOpen();
        if (!e.dataTransfer) return;
        const srcSkStr = e.dataTransfer.getData('text/plain');
        if (!srcSkStr) return;
        const srcSk = parseInt(srcSkStr);
        if (onCardDrop) {
          onCardDrop(srcSk, sk);
        }
      };

      el.addEventListener('dragover', handleDragOver);
      el.addEventListener('dragleave', handleDragLeave);
      el.addEventListener('drop', handleDrop);

      handlers[sk] = { dragover: handleDragOver, dragleave: handleDragLeave, drop: handleDrop };
    });

    // A drag that ends anywhere else (Escape, dropped outside the grid) must
    // not open a row afterwards.
    document.addEventListener('dragend', cancelDragOpen);

    return () => {
      document.removeEventListener('dragend', cancelDragOpen);
      cancelDragOpen();
      Object.entries(handlers).forEach(([skStr, h]) => {
        const sk = parseInt(skStr);
        const el = cells[sk] as any;
        if (!el) return;
        el.removeEventListener('dragover', h.dragover);
        el.removeEventListener('dragleave', h.dragleave);
        el.removeEventListener('drop', h.drop);
      });
    };
  }, [onCardDrop]);

  const handleCellPress = (sk: number, ref: View | null) => {
    if (!ref || (!onEmptyPress && !onEntryPress)) {
      onSlotPress(sk);
      return;
    }

    ref.measureInWindow((x, y, width, height) => {
      const plannedMeal = slotsMap.get(sk);
      if (plannedMeal && isEntryMeal(plannedMeal)) {
        onEntryPress?.(sk, { x, y, width, height });
      } else {
        onEmptyPress?.(sk, { x, y, width, height });
      }
    });
  };

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
      onLayout={(e) => setViewportH(e.nativeEvent.layout.height)}
    >
      {/* Day header row */}
      <View onLayout={(e) => setHeaderH(e.nativeEvent.layout.height)} style={[styles.headerRow, isWeb && ({ display: 'grid', gridTemplateColumns: '52px repeat(7, minmax(0, 1fr))', gap: 8 } as any)]}>
        <View style={styles.rowLabelCell} />
        {DAY_SHORT_KEYS.map((_dayKey, di) => {
          const cellIso = addDays(weekStart, di);
          const dayNum = parseIsoDate(cellIso).getDate();
          const isToday = cellIso === todayIso();
          const isTargetDay = !!placeTarget && dayOf(placeTarget.sk) === di;
          return (
            <View key={di} style={styles.dayHeaderCell}>
              <Text style={[styles.dayHeaderText, isTargetDay && styles.targetDay]}>{t(dayShortKey(di))}</Text>
              <Text style={[styles.dayNumberText, isToday && styles.dayNumberToday, isTargetDay && styles.targetDay]}>{dayNum}</Text>
              {/* A bold day number was the only "today" marker, and it reads as
                  emphasis rather than as a date (#579). */}
              {isToday && (
                <View style={styles.todayPill}>
                  <Text style={styles.todayPillText}>{t('thisWeek.today')}</Text>
                </View>
              )}
              {onAddToDay && canAddToDay?.(di) && (
                <DayAddButton
                  label={t('thisWeek.addToDay')}
                  accessibilityLabel={t('thisWeek.addToDayLabel', { day: t(dayFullKey(di)) })}
                  testID={`day-add-${di}`}
                  onPress={() => onAddToDay(di)}
                />
              )}
            </View>
          );
        })}
      </View>

      {/* Meal rows */}
      {MEALS.map((meal, mi) => {
        const isOpen = mi === focusRow;
        return (
        <View
          key={meal.id}
          ref={(el) => { rowRefs.current[mi] = el; }}
          testID={`meal-row-${mi}`}
          style={[
            styles.mealRow,
            isOpen ? { minHeight: openH } : styles.mealRowClosed,
            // The track is sized, not the box: RNW gives every View
            // `align-content: flex-start`, so a min-height on the grid alone
            // leaves the row's cards at their own height under empty space.
            isWeb && ({
              display: 'grid',
              gridTemplateColumns: '52px repeat(7, minmax(0, 1fr))',
              gridAutoRows: `minmax(${isOpen ? openH : COMPACT_ROW_HEIGHT}px, auto)`,
              gap: 8,
            } as any),
          ]}
        >
          {/* Row label — meal type. On a closed row it is also the way to open it. */}
          <Pressable
            style={styles.rowLabelCell}
            testID={`meal-row-label-${mi}`}
            disabled={isOpen}
            onPress={() => onFocusRow(mi as FocusRow, 'label')}
            accessibilityRole="button"
            accessibilityState={{ expanded: isOpen }}
            accessibilityLabel={isOpen ? t(meal.labelKey) : t('thisWeek.openRow', { meal: t(meal.labelKey) })}
          >
            <View style={styles.rowLabelInner}>
              <Text style={[styles.rowLabel, isOpen && styles.rowLabelOpen]} numberOfLines={1}>{t(meal.labelKey)}</Text>
              {!isOpen && <Feather name="chevron-right" size={11} color={Paprika.textMuted} />}
            </View>
          </Pressable>

          {/* 7 slot cells */}
          {[0, 1, 2, 3, 4, 5, 6].map((di) => {
            const sk = slotKey(mi, di);
            const cellRef = (el: View | null) => { if (el === null) delete cellRefs.current[sk]; else cellRefs.current[sk] = el; };

            if (!isOpen) {
              // A closed row: what the slot holds, in a strip. Clicking it opens
              // the row; an empty strip also starts filling its slot.
              return (
                <View key={di} style={styles.cell} testID={`slot-${sk}`} ref={cellRef}>
                  <SlotMiniCell
                    sk={sk}
                    meal={slotsMap.get(sk)}
                    slotMeals={slotMeals?.get(sk) ?? []}
                    recipeIndex={recipeIndex}
                    isNext={nextSk === sk}
                    flash={flash === sk}
                    dragOver={dragOverSk === sk}
                    onOpen={() => onFocusRow(mi as FocusRow, 'card')}
                    onAdd={() => {
                      onFocusRow(mi as FocusRow, 'add');
                      if (onEmptyPress) handleCellPress(sk, cellRefs.current[sk]);
                      else onSlotPress(sk);
                    }}
                  />
                </View>
              );
            }

            const isWeekendBreakfast = meal.weekendOnly && di < 5;
            const plannedMeal = slotsMap.get(sk);
            const recipe = plannedMeal ? (recipeIndex[plannedMeal.recipeId] ?? null) : null;
            const isEntry = plannedMeal && isEntryMeal(plannedMeal);
            const isTarget = placeTarget?.sk === sk;

            if (isTarget && placeTarget?.preview) {
              return (
                <View key={di} style={styles.cell} testID={`slot-${sk}`} ref={(el) => { if (el === null) delete cellRefs.current[sk]; else cellRefs.current[sk] = el; }}>
                  <SlotPreviewCard recipe={placeTarget.preview} />
                </View>
              );
            }

            if (isWeekendBreakfast) {
              return (
                <View key={di} style={styles.cell} testID={`slot-${sk}`} ref={(el) => { if (el === null) delete cellRefs.current[sk]; else cellRefs.current[sk] = el; }}>
                  <EmptySlot
                    variant="faint"
                    onPress={onEmptyPress ? () => handleCellPress(sk, cellRefs.current[sk]) : undefined}
                  />
                </View>
              );
            }

            // Extra dishes stacked in the same slot (main + side): they used
            // to be invisible, they now list under the primary card.
            const extras = plannedMeal ? (slotMeals?.get(sk) ?? []).filter((m) => m.id !== plannedMeal.id) : [];
            const renderExtras = () => extras.map((extra) => {
              const extraRecipe = recipeIndex[extra.recipeId];
              if (!extraRecipe) return null;
              return (
                <SlotExtraCard
                  key={extra.id}
                  recipe={extraRecipe}
                  meal={extra}
                  isCooked={extra.status === 'cooked'}
                  onPress={() => onExtraPress?.(extra)}
                  onRemove={onRemoveExtra ? () => onRemoveExtra(extra) : undefined}
                />
              );
            });
            // A lunch or a dinner with nothing beside it yet — a recipe, or a
            // note / leftovers. Once a side is listed, the list of
            // accompaniments is one click on it away.
            const offersSide = !!onMoreSides && mi >= 1 && extras.length === 0
              && (!!recipe || (!!plannedMeal && isSideableEntryMeal(plannedMeal)))
              && !moving && !placeTarget && dragOverSk === null && canAddDish(slotMeals?.get(sk));
            const showOffer = offersSide && (!canHover || hoverSk === sk);
            const sideOffer = showOffer && (
              <SlotSideOffer
                side={canHover ? hoverSide : null}
                floating={canHover}
                onAdd={() => { if (hoverSide) onAddSide?.(sk, hoverSide); }}
                onMore={() => onMoreSides!(sk)}
              />
            );

            if (isEntry) {
              return (
                <View key={di} style={[styles.cell, showOffer && canHover && styles.cellRaised]} testID={`slot-${sk}`} ref={(el) => { if (el === null) delete cellRefs.current[sk]; else cellRefs.current[sk] = el; }}>
                  <EntryCard
                    sk={sk}
                    entry={plannedMeal.entry!}
                    resolveTitle={(id) => recipeIndex[id]?.title}
                    active={menuFor === sk}
                    onPress={() => handleCellPress(sk, cellRefs.current[sk])}
                  />
                  {renderExtras()}
                  {sideOffer}
                </View>
              );
            }

            if (recipe) {
              return (
                <View key={di} style={[styles.cell, showOffer && canHover && styles.cellRaised]} testID={`slot-${sk}`} ref={(el) => { if (el === null) delete cellRefs.current[sk]; else cellRefs.current[sk] = el; }}>
                  <SlotCard
                    sk={sk}
                    recipe={recipe}
                    mealLabel={t(meal.labelKey)}
                    dietaryRole={plannedMeal?.dietaryRole}
                    flash={flash === sk}
                    dragOver={dragOverSk === sk}
                    isNext={nextSk === sk}
                    isCooked={plannedMeal?.status === 'cooked'}
                    mealId={plannedMeal?.id}
                    onPhotoPress={() => onPhotoPress(sk)}
                    onCardPress={() => onSlotPress(sk)}
                    onRemove={onRemoveSlot ? () => onRemoveSlot(sk) : undefined}
                  />
                  {renderExtras()}
                  {sideOffer}
                </View>
              );
            }

            // Check if there are proposals but no accepted meal
            const slotAllMeals = slotMeals?.get(sk) ?? [];
            const hasProposals = proposalsOf(slotAllMeals).length > 0;
            const hasAccepted = slotHasAcceptedMeal(slotAllMeals);

            if (hasProposals && !hasAccepted) {
              // The caption explains why there is no big card; the proposals
              // themselves render underneath it with their own accept/dismiss
              // controls, and the slot keeps its add affordance so it is never
              // a dead end.
              const proposals = proposalsOf(slotAllMeals);
              return (
                <View key={di} style={styles.cell} testID={`slot-${sk}`} ref={(el) => { if (el === null) delete cellRefs.current[sk]; else cellRefs.current[sk] = el; }}>
                  <View style={styles.proposalsEmptySlot}>
                    <Text style={styles.noneOnMenuText}>{t('household.approval.noneOnMenu')}</Text>
                    <Text style={styles.toSettleText}>
                      {t('household.approval.toSettle', { count: proposals.length })}
                    </Text>
                  </View>
                  {proposals.map((proposal) => {
                    const proposalRecipe = recipeIndex[proposal.recipeId];
                    if (!proposalRecipe) return null;
                    return (
                      <SlotExtraCard
                        key={proposal.id}
                        recipe={proposalRecipe}
                        meal={proposal}
                        onPress={() => onExtraPress?.(proposal)}
                        onRemove={onRemoveExtra ? () => onRemoveExtra(proposal) : undefined}
                      />
                    );
                  })}
                  <EmptySlot
                    variant={dragOverSk === sk ? 'glow' : 'normal'}
                    onPress={onEmptyPress ? () => handleCellPress(sk, cellRefs.current[sk]) : () => onSlotPress(sk)}
                  />
                </View>
              );
            }

            return (
              <View key={di} style={styles.cell} testID={`slot-${sk}`} ref={(el) => { if (el === null) delete cellRefs.current[sk]; else cellRefs.current[sk] = el; }}>
                <EmptySlot
                  variant={dragOverSk === sk || isTarget ? 'glow' : 'normal'}
                  onPress={onEmptyPress ? () => handleCellPress(sk, cellRefs.current[sk]) : () => onSlotPress(sk)}
                />
              </View>
            );
          })}
        </View>
        );
      })}
    </ScrollView>
  );
}

/**
 * Quiet until pointed at, so seven of them don't shout over the grid. The
 * hover lives on the button itself: nothing is nested inside it, so RNW's
 * hover lock (a child Pressable un-hovering its parent) cannot bite.
 */
function DayAddButton({ label, accessibilityLabel, testID, onPress }: {
  label: string;
  accessibilityLabel: string;
  testID: string;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      style={[styles.dayAdd, hover && styles.dayAddHover]}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      testID={testID}
    >
      <Feather name="plus" size={11} color={hover ? Paprika.primary : Paprika.textMuted} />
      <Text style={[styles.dayAddText, hover && styles.dayAddTextHover]}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  // Lifts the hovered cell over the row below, which its floating offer overlaps.
  cellRaised: {
    zIndex: 5,
  },
  targetDay: {
    color: Paprika.primary,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    gap: 8,
  },
  headerRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 4,
    alignItems: 'center',
  },
  mealRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'stretch',
    minHeight: 88,
    marginBottom: 8,
  },
  mealRowClosed: {
    minHeight: COMPACT_ROW_HEIGHT,
  },
  rowLabelCell: {
    width: 52,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Rotated as one piece, so the chevron of a closed row reads along the label.
  // Wider than the cell on purpose (centred, it overflows evenly): the label
  // is measured before it is rotated, and 52px would ellipsise "Breakfast".
  rowLabelInner: {
    width: 110,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    transform: [{ rotate: '-90deg' }],
  },
  rowLabel: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 12,
    color: Paprika.textMuted,
    textAlign: 'center',
  },
  rowLabelOpen: {
    fontSize: 15,
    color: Paprika.primary,
  },
  dayHeaderCell: {
    flex: 1,
    alignItems: 'center',
    paddingBottom: 4,
  },
  todayPill: {
    marginTop: 3,
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 20,
    backgroundColor: Paprika.accentLight,
  },
  todayPillText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 9,
    letterSpacing: 0.7,
    textTransform: 'uppercase',
    color: Paprika.primary,
  },
  dayAdd: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    marginTop: 4,
    paddingVertical: 3,
    paddingHorizontal: 9,
    borderRadius: 20,
    ...(isWeb && { cursor: 'pointer' } as any),
  },
  dayAddHover: {
    backgroundColor: Paprika.surfaceLowest,
    ...(isWeb && { boxShadow: '0 2px 8px rgba(82,68,60,0.08)' } as any),
  },
  dayAddText: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    color: Paprika.textMuted,
  },
  dayAddTextHover: {
    color: Paprika.primary,
  },
  dayHeaderText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
  },
  dayNumberText: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
    marginTop: 2,
  },
  dayNumberToday: {
    fontFamily: 'Inter-SemiBold',
    color: Paprika.primary,
  },
  cell: {
    flex: 1,
    gap: 4,
  },
  // A caption, not a box: the proposal cards render under it and already carry
  // the dashed treatment, so a second dashed rule around the text would be a
  // line doing a job spacing already does.
  proposalsEmptySlot: {
    paddingVertical: 6,
    paddingHorizontal: 4,
    alignItems: 'center',
    gap: 2,
  },
  noneOnMenuText: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
  },
  toSettleText: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    color: Paprika.approvalIdea,
  },
});
